import {
  getManualAllocationPayments,
  getMembers,
  getObligationPortfolio,
  getPaymentHistory,
  type ManualAllocationListItem,
  type PaymentHistoryItem,
} from '@urbanik/api-client';
import {
  AlertCircle,
  ArrowRight,
  CircleDollarSign,
  ReceiptText,
  ShieldCheck,
  TrendingDown,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { apiBase } from './api-base';
import './admin-dashboard.css';

type DashboardData = {
  activeMembers: number;
  pendingPayments: ManualAllocationListItem[];
  recentPayments: PaymentHistoryItem[];
  paymentTotal: number;
  delinquency: {
    memberCount: number;
    totalAmount: string;
    members: readonly {
      memberId: string;
      memberName: string;
      obligationCount: number;
      amount: string;
    }[];
  };
  obligationTotal: string;
  slotCapacity: number | null;
  availableSlots: number | null;
};

const money = (value: string) =>
  new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(Number(value));

export function AdminDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    Promise.all([
      getMembers(apiBase, { status: 'active', page: 1, pageSize: 5 }),
      getManualAllocationPayments(apiBase),
      getPaymentHistory(apiBase, { page: 1, pageSize: 50 }),
      getObligationPortfolio(apiBase, { page: 1, pageSize: 50 }),
    ])
      .then(([members, pending, history, portfolio]) => {
        if (!active) return;
        setData({
          activeMembers: members.totalItems,
          pendingPayments: pending.items,
          recentPayments: history.items,
          paymentTotal: history.total,
          delinquency: portfolio.delinquencySummary ?? {
            memberCount: 0,
            totalAmount: '0.00',
            members: [],
          },
          obligationTotal: portfolio.totalAmount,
          slotCapacity: members.summary?.slotCapacity ? members.summary.slotCapacity : null,
          availableSlots: members.summary?.slotCapacity
            ? (members.summary.availableSlots ?? 0)
            : null,
        });
      })
      .catch((reason) => active && setError((reason as Error).message));
    return () => {
      active = false;
    };
  }, []);

  const paymentMetrics = useMemo(() => {
    const items = data?.recentPayments ?? [];
    const approved = items.filter((item) => item.status === 'applied');
    const reversed = items.filter((item) => item.status === 'reversed');
    const approvedValue = approved.reduce((total, item) => total + Number(item.amount), 0);
    const months = Array.from({ length: 12 }, (_, index) => ({
      label: new Intl.DateTimeFormat('es-EC', { month: 'short' }).format(new Date(2026, index, 1)),
      value: 0,
    }));
    items.forEach((item) => {
      const date = new Date(item.registeredAt);
      if (!Number.isNaN(date.getTime()) && date.getFullYear() === 2026)
        months[date.getMonth()]!.value += Number(item.amount);
    });
    return {
      approved,
      reversed,
      approvedValue,
      months,
      maximum: Math.max(...months.map((month) => month.value), 1),
    };
  }, [data]);

  const overdueMembers = data?.delinquency.members ?? [];
  const availableSlots = data?.availableSlots ?? null;

  return (
    <main className="admin-dashboard">
      <header className="admin-dashboard__heading">
        <div>
          <span>RESUMEN ADMINISTRATIVO</span>
          <h1>Dashboard</h1>
          <p>Información prioritaria para gestionar socios, pagos y accesos.</p>
        </div>
        <small>Actualizado al abrir esta pantalla</small>
      </header>

      {error ? (
        <div className="admin-dashboard__error" role="alert">
          <AlertCircle aria-hidden="true" />
          <span>
            <strong>No se pudo cargar el resumen.</strong>
            {error}
          </span>
        </div>
      ) : null}

      <section className="admin-dashboard__stats" aria-label="Indicadores principales">
        <article>
          <Users />
          <span>
            Socios activos<strong>{data?.activeMembers ?? '—'}</strong>
            <small>Padrón vigente</small>
          </span>
        </article>
        <article>
          <ReceiptText />
          <span>
            Por revisar<strong>{data?.pendingPayments.length ?? '—'}</strong>
            <small>Comprobantes pendientes</small>
          </span>
        </article>
        <article>
          <ShieldCheck />
          <span>
            Historial de pagos<strong>{data?.paymentTotal ?? '—'}</strong>
            <small>Registros auditados</small>
          </span>
        </article>
        <article>
          <ReceiptText />
          <span>
            Valor aprobado
            <strong>{data ? money(String(paymentMetrics.approvedValue)) : '—'}</strong>
            <small>Últimos 50 registros</small>
          </span>
        </article>
        <article>
          <AlertCircle />
          <span>
            Reversados<strong>{data ? paymentMetrics.reversed.length : '—'}</strong>
            <small>Requieren seguimiento</small>
          </span>
        </article>{' '}
        <article>
          <Users />
          <span>
            Cupos disponibles<strong>{availableSlots ?? 'Sin definir'}</strong>
            <small>
              {data?.slotCapacity
                ? `de  cupos configurados`
                : 'Configurable al crear o editar la entidad'}
            </small>
          </span>
        </article>
      </section>

      <section className="admin-dashboard__charts" aria-label="Análisis gráfico">
        <article>
          <header>
            <div>
              <span>COMPROBANTES 2026</span>
              <h2>Evolución mensual</h2>
            </div>
            <small>Valores registrados</small>
          </header>
          <div
            className="admin-dashboard__bars"
            role="img"
            aria-label="Valores registrados por mes"
          >
            {paymentMetrics.months.map((month) => (
              <div key={month.label}>
                <span
                  style={{
                    height: Math.max(5, (month.value / paymentMetrics.maximum) * 100) + '%',
                  }}
                  title={money(String(month.value))}
                />
                <small>{month.label.replace('.', '')}</small>
              </div>
            ))}
          </div>
          <footer>
            <i /> Registrado
          </footer>
        </article>
        <article>
          <header>
            <div>
              <span>CONTROL DE PAGOS</span>
              <h2>Comprobantes por estado</h2>
            </div>
          </header>
          <div className="admin-dashboard__donut-wrap">
            <div
              className="admin-dashboard__donut"
              style={
                {
                  '--approved':
                    Math.round(
                      (paymentMetrics.approved.length /
                        Math.max(data?.recentPayments.length ?? 0, 1)) *
                        100,
                    ) + '%',
                } as CSSProperties
              }
            >
              <span>
                <strong>{data?.recentPayments.length ?? 0}</strong>
                <small>Total</small>
              </span>
            </div>
            <ul>
              <li>
                <i data-tone="success" />
                <span>Aprobados</span>
                <strong>{paymentMetrics.approved.length}</strong>
              </li>
              <li>
                <i data-tone="warning" />
                <span>Pendientes</span>
                <strong>{data?.pendingPayments.length ?? 0}</strong>
              </li>
              <li>
                <i data-tone="danger" />
                <span>Reversados</span>
                <strong>{paymentMetrics.reversed.length}</strong>
              </li>
            </ul>
          </div>
          <a href="#pagos">
            Ver detalle de pagos <ArrowRight />
          </a>
        </article>
      </section>
      <section className="admin-dashboard__activity" aria-labelledby="activity-title">
        <header>
          <div>
            <span>ACTIVIDAD RECIENTE</span>
            <h2 id="activity-title">Últimos pagos registrados</h2>
          </div>
          <a href="#pagos">
            Ver todos <ArrowRight />
          </a>
        </header>
        {data?.recentPayments.length ? (
          <ul>
            {data.recentPayments.slice(0, 5).map((payment) => (
              <li key={payment.id}>
                <ReceiptText aria-hidden="true" />
                <span>
                  <strong>{payment.memberName}</strong>
                  <small>Referencia {payment.reference}</small>
                </span>
                <b>{money(payment.amount)}</b>
                <em data-status={payment.status}>
                  {payment.status === 'applied'
                    ? 'Aprobado y registrado'
                    : payment.status === 'rejected'
                      ? 'Rechazado'
                      : payment.status === 'reversed'
                        ? 'Reversado'
                        : 'En revisión'}
                </em>
              </li>
            ))}
          </ul>
        ) : (
          <p className="admin-dashboard__empty">Todavía no existen comprobantes registrados.</p>
        )}
      </section>
      <section className="admin-dashboard__secondary" aria-label="Indicadores financieros">
        <article className="admin-dashboard__arrears">
          <header>
            <div>
              <span>SEGUIMIENTO DE CARTERA</span>
              <h2>Socios con morosidad</h2>
              <small className="admin-dashboard__arrears-summary">
                {data
                  ? `${data.delinquency.memberCount} socios con saldo vencido · mayores saldos`
                  : 'Calculando cartera…'}
              </small>
            </div>
            <a href="#cartera">
              Ver cartera <ArrowRight />
            </a>
          </header>
          {overdueMembers.length ? (
            <ul>
              {overdueMembers.map((member) => (
                <li key={member.memberId}>
                  <span aria-hidden="true">{member.memberName.slice(0, 2).toUpperCase()}</span>
                  <div>
                    <strong>{member.memberName}</strong>
                    <small>{member.obligationCount} obligaciones vencidas</small>
                  </div>
                  <b>{money(String(member.amount))}</b>
                </li>
              ))}
            </ul>
          ) : (
            <p className="admin-dashboard__empty">No se identificaron obligaciones vencidas.</p>
          )}
        </article>
        <article className="admin-dashboard__cashflow">
          <header>
            <div>
              <span>RESUMEN FINANCIERO</span>
              <h2>Ingresos y egresos</h2>
            </div>
            <CircleDollarSign aria-hidden="true" />
          </header>
          <dl>
            <div>
              <dt>Ingresos aprobados</dt>
              <dd data-tone="success">{money(String(paymentMetrics.approvedValue))}</dd>
            </div>
            <div>
              <dt>Cartera registrada</dt>
              <dd>{money(data?.obligationTotal ?? '0')}</dd>
            </div>
            <div>
              <dt>Egresos</dt>
              <dd data-tone="pending">Pendiente de integración</dd>
            </div>
          </dl>
          <div className="admin-dashboard__cashflow-note">
            <TrendingDown aria-hidden="true" />
            <span>El sistema aún no dispone de un módulo contable de egresos.</span>
          </div>
        </article>
      </section>
    </main>
  );
}
